import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches errors thrown when React Router context is missing or duplicated
 * (e.g. "Cannot destructure property 'basename' of React.useContext(...) as it is null").
 * Shows a friendly recovery page instead of a blank white screen.
 */
export class RouterErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("RouterErrorBoundary caught:", error, info);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message ?? "";
    const isRouterContextError =
      message.includes("basename") ||
      message.includes("useContext") ||
      message.includes("Router");

    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <h1 className="mb-3 text-3xl font-bold text-foreground">
            Something went wrong
          </h1>
          <p className="mb-6 text-muted-foreground">
            {isRouterContextError
              ? "The app's navigation system failed to load. This usually clears up after a refresh."
              : "An unexpected error occurred. Please try refreshing the page."}
          </p>
          <button
            onClick={this.handleReload}
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Reload page
          </button>
          {this.state.error && (
            <details className="mt-6 text-left text-xs text-muted-foreground">
              <summary className="cursor-pointer">Technical details</summary>
              <pre className="mt-2 overflow-auto rounded bg-muted p-3">
                {this.state.error.message}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
